/**
 * JLR SharePoint 的交互认证弹窗（Pike 项目）。
 *
 * 该站点强制 MFA：密码直连会被 AADSTS50076 拒绝，用户必须在 Microsoft
 * Authenticator 里输入登录页上显示的数字并批准。所以「上传第三方平台」在这里拆成
 * 两步：先填凭据拿号码，用户批准后再带着会话去建上传任务。
 *
 * 密码只进这一次请求，不回显、不落任何本地存储。
 */

import { useEffect, useState } from 'react';
import { Alert, Button, Form, Input, Modal, Space, Typography } from 'antd';
import { KeyOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import type { JlrAuthStartResult } from '@/types';
import { startJlrAuth } from '@/api/thirdPartyUpload';
import { useT } from '@/i18n';

const { Text, Paragraph } = Typography;

/** 后端 `config.py` 里的默认父目录；用户可改，改错了后端会报「父目录不存在」 */
export const DEFAULT_JLR_PARENT_URL =
  'https://jlrglobal.sharepoint.com/sites/jlr-momenta-aad/Shared%20Documents/Forms/AllItems.aspx' +
  '?id=%2Fsites%2Fjlr%2Dmomenta%2Daad%2FShared%20Documents%2F01%5FSupplier%20Exchange' +
  '%2F02%5FBack%20JLR%20From%20Momenta%2F05%20Integration%2Fpackage%5Frelease' +
  '&viewid=3572b730%2D9b6c%2D4e30%2D91f8%2D1a2812c4f9a6';

export interface JlrAuthPayload {
  authSession: string;
  parentUrl: string;
  folder: string;
}

interface JlrAuthFormValues {
  username: string;
  password: string;
  parent_url: string;
  folder: string;
}

interface JlrAuthModalProps {
  open: boolean;
  releaseId: number;
  /** 用户已批准：带会话去建上传任务。抛异常时弹窗保持打开并显示原因 */
  onConfirm: (payload: JlrAuthPayload) => Promise<void>;
  onCancel: () => void;
}

const errorText = (e: unknown, fallback: string): string =>
  (e as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

export const JlrAuthModal: React.FC<JlrAuthModalProps> = ({
  open,
  releaseId,
  onConfirm,
  onCancel,
}) => {
  const { t } = useT();
  const [form] = Form.useForm<JlrAuthFormValues>();
  // 'credentials' = 填账号密码；'approve' = 等用户在手机上输数字并批准
  const [step, setStep] = useState<'credentials' | 'approve'>('credentials');
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const [auth, setAuth] = useState<JlrAuthStartResult | null>(null);
  // 只留回填需要的三项：密码不留在 React state 里，返回修改时要重新输入
  const [values, setValues] = useState<Omit<JlrAuthFormValues, 'password'> | null>(null);

  // 每次重新打开都回到第一步：上一次的会话 ID 已经跟着上传任务消费掉了
  useEffect(() => {
    if (!open) return;
    setStep('credentials');
    setAuth(null);
    setValues(null);
    setError('');
    setSubmitting(false);
    setConfirming(false);
  }, [open]);

  const handleStart = async () => {
    let formValues: JlrAuthFormValues;
    try {
      formValues = await form.validateFields();
    } catch {
      return; // 表单校验失败，Form 已就地提示
    }
    setSubmitting(true);
    setError('');
    try {
      const result = await startJlrAuth(releaseId, {
        username: formValues.username.trim(),
        password: formValues.password,
      });
      setAuth(result);
      setValues({ username: formValues.username, parent_url: formValues.parent_url,
                  folder: formValues.folder });
      setStep('approve');
    } catch (e) {
      setError(errorText(e, t('启动认证失败，请检查账号密码后重试')));
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async () => {
    if (!auth || !values) return;
    setConfirming(true);
    setError('');
    try {
      await onConfirm({
        authSession: auth.session_id,
        parentUrl: values.parent_url.trim(),
        folder: values.folder.trim(),
      });
      onCancel();
    } catch (e) {
      // 未批准（409 auth_pending）或会话过期：留在弹窗里，用户可以再点一次
      setError(errorText(e, t('确认上传失败，请重试')));
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Modal
      title={t('上传到 JLR SharePoint')}
      open={open}
      onCancel={onCancel}
      footer={null}
      destroyOnHidden
      maskClosable={false}
      width={620}
    >
      {step === 'credentials' ? (
        <Form
          form={form}
          layout="vertical"
          autoComplete="off"
          preserve={false}
          initialValues={{
            username: values?.username ?? '',
            password: '',
            parent_url: values?.parent_url ?? DEFAULT_JLR_PARENT_URL,
            folder: values?.folder ?? '',
          }}
          onFinish={handleStart}
        >
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('该平台需要 Microsoft 账号认证')}
            description={t('提交账号密码后，系统会生成一个校验数字；请在手机的 Microsoft Authenticator 里输入该数字并批准，然后回来点击「确认上传」。密码仅在本次请求中转发，不会保存在 BSP。')}
          />
          <Form.Item
            label={t('用户名')}
            name="username"
            rules={[{ required: true, whitespace: true, message: t('请填写账号') }]}
          >
            <Input placeholder={t('例如 wenqiang.zhao.momenta@jlrglobal.onmicrosoft.com')} autoComplete="off" />
          </Form.Item>
          <Form.Item
            label={t('密码')}
            name="password"
            rules={[{ required: true, message: t('请填写密码') }]}
          >
            <Input.Password placeholder={t('Microsoft 账号密码')} autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            label={t('父目录链接')}
            name="parent_url"
            rules={[
              { required: true, whitespace: true, message: t('请填写父目录链接') },
              { max: 500, message: t('父目录链接不能超过 500 个字符') },
            ]}
            extra={t('默认已填好 package_release 目录，一般不需要改。')}
          >
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
          <Form.Item
            label={t('文件夹')}
            name="folder"
            rules={[
              { required: true, whitespace: true, message: t('请填写目标文件夹') },
              { max: 255, message: t('文件夹不能超过 255 个字符') },
            ]}
            extra={t('父目录下的文件夹名，支持多级（如 drop12/sw1/pike）。不存在会自动新建；目录里已有同名文件时整批失败，不覆盖。')}
          >
            <Input placeholder={t('例如 drop12/sw1/pike_20261029')} />
          </Form.Item>

          {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}

          <Space className="flex justify-end w-full">
            <Button onClick={onCancel}>{t('取消')}</Button>
            <Button type="primary" icon={<KeyOutlined />} loading={submitting} onClick={handleStart}>
              {t('提交并获取校验数字')}
            </Button>
          </Space>
        </Form>
      ) : (
        <div>
          <Alert
            type="warning"
            showIcon
            icon={<SafetyCertificateOutlined />}
            message={t('请在 Microsoft Authenticator 中批准登录')}
            description={
              auth?.sign_number
                ? t('打开手机上的 Microsoft Authenticator，在弹窗里输入下面这个数字，然后点「批准」。批准完成后再点下方的「确认上传」。')
                : t('认证已直接完成，可点击下方「确认上传」。')
            }
            style={{ marginBottom: 16 }}
          />

          {auth?.sign_number && (
            <div className="text-center my-6">
              <Text type="secondary">{t('校验数字')}</Text>
              <div
                className="font-bold tracking-widest"
                style={{ fontSize: 48, lineHeight: 1.1, letterSpacing: '0.25em' }}
              >
                {auth.sign_number}
              </div>
            </div>
          )}

          {values && (
            <Paragraph type="secondary" style={{ marginBottom: 12 }}>
              {t('上传目录：')}<Text code>{values.folder}</Text>
            </Paragraph>
          )}

          {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}

          <Space className="flex justify-end w-full">
            <Button onClick={() => setStep('credentials')} disabled={confirming}>
              {t('返回修改')}
            </Button>
            <Button type="primary" loading={confirming} onClick={handleConfirm}>
              {t('确认上传')}
            </Button>
          </Space>
        </div>
      )}
    </Modal>
  );
};

export default JlrAuthModal;
